"use client";

import { Minus, MonitorDown, Plus, RotateCcw } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import {
  clampConsoleFontSize,
  clampFontSize,
  CONSOLE_FONT_SIZE_MAX,
  CONSOLE_FONT_SIZE_MIN,
  DEFAULT_EDITOR_SETTINGS,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  type EditorSettings,
} from "../lib/editor-settings";
import { useLanguageGuideVisibility } from "../lib/language-guide-storage";
import { cn } from "@/lib/utils";

type EditorSettingsPanelProps = {
  editorSettings: EditorSettings;
  onEditorSettingsChange: (next: EditorSettings) => void;
  /** Language SEO pages only — toggles the guide article below the IDE. */
  showLanguageGuideToggle?: boolean;
  /**
   * Runtime version + compiler flags (`PG-IDE01`).
   *
   * Injected rather than built here because it needs the run state the client
   * owns, and because both settings surfaces — the desktop gear and the mobile
   * overflow submenu — must show the same one.
   */
  runtimePanel?: ReactNode;
  /**
   * Install Progsity IDE (`PG-PWA01`). Present only while the browser holds an
   * install prompt for this page; absent, nothing renders, so there is never
   * a dead "install" control on a browser that cannot.
   */
  onInstall?: () => void;
  className?: string;
};

/**
 * Editor preferences (font, theme, tab, wrap, minimap) — shared by toolbar gear menu and narrow-viewport overflow submenu.
 */
export function EditorSettingsPanel({
  editorSettings,
  onEditorSettingsChange,
  showLanguageGuideToggle = false,
  runtimePanel,
  onInstall,
  className,
}: EditorSettingsPanelProps) {
  const t = useTranslations("playground");
  const { hidden: guideHidden, setHidden: setGuideHidden } = useLanguageGuideVisibility();

  const setFontSize = (n: number) => {
    onEditorSettingsChange({
      ...editorSettings,
      fontSize: clampFontSize(n),
    });
  };

  const setConsoleFontSize = (n: number) => {
    onEditorSettingsChange({
      ...editorSettings,
      consoleFontSize: clampConsoleFontSize(n),
    });
  };

  return (
    <div className={cn("space-y-3 text-sm text-white/90", className)}>
      {runtimePanel ? (
        <div className="border-b border-white/10 pb-3">{runtimePanel}</div>
      ) : null}
      <div>
        <label className="mb-1 block text-xs text-white/55" htmlFor="pg-font-size">
          {t("settingsFontSize")}
        </label>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            id="pg-font-size-dec"
            aria-label={t("settingsFontSizeDecrease")}
            onClick={() => setFontSize(editorSettings.fontSize - 1)}
            disabled={editorSettings.fontSize <= FONT_SIZE_MIN}
            className={cn(
              "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-[#0B0B0C] text-white transition",
              "hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35",
            )}
          >
            <Minus className="h-4 w-4" aria-hidden />
          </button>
          <input
            id="pg-font-size"
            type="number"
            inputMode="numeric"
            min={FONT_SIZE_MIN}
            max={FONT_SIZE_MAX}
            step={1}
            value={editorSettings.fontSize}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return;
              const v = Number.parseInt(raw, 10);
              if (Number.isNaN(v)) return;
              setFontSize(v);
            }}
            className="h-9 min-w-0 flex-1 rounded-lg border border-white/15 bg-[#0B0B0C] px-2 text-center text-sm tabular-nums text-white outline-none [appearance:textfield] focus:border-[#E94D1D]/50 focus:ring-2 focus:ring-[#E94D1D]/30 focus:ring-offset-2 focus:ring-offset-[#141416] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <button
            type="button"
            id="pg-font-size-inc"
            aria-label={t("settingsFontSizeIncrease")}
            onClick={() => setFontSize(editorSettings.fontSize + 1)}
            disabled={editorSettings.fontSize >= FONT_SIZE_MAX}
            className={cn(
              "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-[#0B0B0C] text-white transition",
              "hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35",
            )}
          >
            <Plus className="h-4 w-4" aria-hidden />
          </button>
          <span className="shrink-0 text-xs text-white/50">px</span>
        </div>
      </div>
      <div>
        <div className="mb-1 flex min-h-11 items-center justify-between gap-2">
          <label className="block text-xs text-white/55" htmlFor="pg-console-font-size">
            {t("settingsConsoleFontSize")}
          </label>
          <button
            type="button"
            aria-label={t("settingsConsoleFontSizeReset")}
            title={t("settingsConsoleFontSizeReset")}
            onClick={() => setConsoleFontSize(DEFAULT_EDITOR_SETTINGS.consoleFontSize)}
            disabled={
              editorSettings.consoleFontSize === DEFAULT_EDITOR_SETTINGS.consoleFontSize
            }
            className={cn(
              "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white/65 transition",
              "hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-35",
            )}
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label={t("settingsConsoleFontSizeDecrease")}
            onClick={() => setConsoleFontSize(editorSettings.consoleFontSize - 1)}
            disabled={editorSettings.consoleFontSize <= CONSOLE_FONT_SIZE_MIN}
            className={cn(
              "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-[#0B0B0C] text-white transition",
              "hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35",
            )}
          >
            <Minus className="h-4 w-4" aria-hidden />
          </button>
          <input
            id="pg-console-font-size"
            type="number"
            inputMode="numeric"
            min={CONSOLE_FONT_SIZE_MIN}
            max={CONSOLE_FONT_SIZE_MAX}
            step={1}
            value={editorSettings.consoleFontSize}
            aria-label={t("settingsConsoleFontSizeValue")}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return;
              const value = Number.parseInt(raw, 10);
              if (Number.isNaN(value)) return;
              setConsoleFontSize(value);
            }}
            className="h-11 min-w-0 flex-1 rounded-lg border border-white/15 bg-[#0B0B0C] px-2 text-center text-base tabular-nums text-white outline-none [appearance:textfield] focus:border-[#E94D1D]/50 focus:ring-2 focus:ring-[#E94D1D]/30 focus:ring-offset-2 focus:ring-offset-[#141416] sm:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <button
            type="button"
            aria-label={t("settingsConsoleFontSizeIncrease")}
            onClick={() => setConsoleFontSize(editorSettings.consoleFontSize + 1)}
            disabled={editorSettings.consoleFontSize >= CONSOLE_FONT_SIZE_MAX}
            className={cn(
              "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-[#0B0B0C] text-white transition",
              "hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35",
            )}
          >
            <Plus className="h-4 w-4" aria-hidden />
          </button>
          <span className="shrink-0 text-xs text-white/50">px</span>
        </div>
      </div>
      <div>
        <div className="mb-1 text-xs text-white/55">{t("settingsTheme")}</div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onEditorSettingsChange({ ...editorSettings, theme: "dark" })}
            className={cn(
              "flex-1 rounded-lg border px-2 py-1.5 text-xs",
              editorSettings.theme === "dark"
                ? "border-[#E94D1D]/60 bg-[#E94D1D]/15 text-white"
                : "border-white/15 bg-white/5 text-white/70",
            )}
          >
            {t("settingsThemeDark")}
          </button>
          <button
            type="button"
            onClick={() => onEditorSettingsChange({ ...editorSettings, theme: "light" })}
            className={cn(
              "flex-1 rounded-lg border px-2 py-1.5 text-xs",
              editorSettings.theme === "light"
                ? "border-[#E94D1D]/60 bg-[#E94D1D]/15 text-white"
                : "border-white/15 bg-white/5 text-white/70",
            )}
          >
            {t("settingsThemeLight")}
          </button>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs text-white/55" htmlFor="pg-tab-size">
          {t("settingsTabSize")}
        </label>
        <select
          id="pg-tab-size"
          value={editorSettings.tabSize}
          onChange={(e) =>
            onEditorSettingsChange({
              ...editorSettings,
              tabSize: Number(e.target.value) as 2 | 4 | 8,
            })
          }
          className="w-full rounded-lg border border-white/15 bg-[#0B0B0C] px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E94D1D]/30"
        >
          <option value={2}>2</option>
          <option value={4}>4</option>
          <option value={8}>8</option>
        </select>
      </div>
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={editorSettings.wordWrap}
          onChange={(e) => onEditorSettingsChange({ ...editorSettings, wordWrap: e.target.checked })}
          className="h-4 w-4 rounded border-white/30 accent-[#E94D1D]"
        />
        <span>{t("settingsWordWrap")}</span>
      </label>
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={editorSettings.minimap}
          onChange={(e) => onEditorSettingsChange({ ...editorSettings, minimap: e.target.checked })}
          className="h-4 w-4 rounded border-white/30 accent-[#E94D1D]"
        />
        <span>{t("settingsMinimap")}</span>
      </label>
      {showLanguageGuideToggle ? (
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={!guideHidden}
            onChange={(e) => setGuideHidden(!e.target.checked)}
            className="h-4 w-4 rounded border-white/30 accent-[#E94D1D]"
          />
          <span>{t("settingsLanguageGuide")}</span>
        </label>
      ) : null}
      {onInstall ? (
        <div className="border-t border-white/10 pt-3">
          <button
            type="button"
            onClick={onInstall}
            title={t("installAppHint")}
            className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-lg border border-[#E94D1D]/50 bg-[#E94D1D]/15 px-3 text-sm font-semibold text-[#FFAB8F] transition hover:bg-[#E94D1D]/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#141416]"
          >
            <MonitorDown className="h-4 w-4 shrink-0" aria-hidden />
            {t("installApp")}
          </button>
          <p className="mt-1.5 text-xs text-white/55">{t("installAppHint")}</p>
        </div>
      ) : null}
      <div className="border-t border-white/10 pt-3">
        <div className="mb-2 text-xs text-white/55">{t("shortcutsTitle")}</div>
        <dl className="space-y-1.5 text-xs">
          <ShortcutRow label={t("shortcutRun")} keys={["Ctrl", "Enter"]} />
          <ShortcutRow label={t("shortcutSave")} keys={["Ctrl", "S"]} />
          <ShortcutRow label={t("shortcutFormat")} keys={["Shift", "Alt", "F"]} />
          <ShortcutRow label={t("shortcutZoom")} keys={["Ctrl", "+ / −"]} />
          <ShortcutRow label={t("shortcutZoomReset")} keys={["Ctrl", "0"]} />
        </dl>
      </div>
    </div>
  );
}

function ShortcutRow({ label, keys }: { label: string; keys: string[] }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-white/70">{label}</dt>
      <dd className="flex items-center gap-1">
        {keys.map((k, i) => (
          <kbd
            key={i}
            className="rounded border border-white/20 bg-white/10 px-1.5 py-0.5 font-mono text-[10px] leading-none text-white/80"
          >
            {k}
          </kbd>
        ))}
      </dd>
    </div>
  );
}
